// Loads non-essential tags, and ONLY once the matching consent has been given.
// Nothing in here runs at page load on its own. It is driven by the cookie banner
// (syncConsent, after a live choice) and by applyStoredConsent() on boot (to
// re-apply a choice a returning visitor already made). This is what keeps tags
// from firing before opt in, per UK PECR / UK GDPR.
//
// Tracker: docs/plans/cookie-banner-implementation.md

import { hasAnalytics, hasMarketing } from '@/utils/consent';
import { eventBus } from '@/utils/eventBus';

const FB_PIXEL_ID = '4216693795087056';
let marketingLoaded = false;
let analyticsLoaded = false;

function deleteCookie(name) {
  if (typeof document === 'undefined') return;
  document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
}

// Marketing: Facebook / Meta Pixel. Moved out of public/index.html (where it used
// to fire on every page load) so it now only initialises after marketing consent.
export function loadMarketing() {
  if (marketingLoaded || typeof window === 'undefined') return;
  marketingLoaded = true;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', FB_PIXEL_ID);
  window.fbq('track', 'PageView');
  /* eslint-enable */
}

// Analytics: SEAM for Google Tag Manager.
// GTM is still loaded unconditionally in public/index.html for now. Moving it to a
// Newco owned Google account (and the exact gating) is a separate, deferred
// decision (see the tracker doc). When that is settled, inject the GTM loader HERE
// so analytics only starts after this call, and remove the snippet from index.html.
export function loadAnalytics() {
  if (analyticsLoaded) return;
  analyticsLoaded = true;

  // GTM is injected HERE rather than in index.html, so nothing it contains can
  // set cookies before the visitor opts in. The container can load GA, Ads,
  // Meta and more from remote config we do not hold in this repo, so leaving
  // the snippet in the page meant an unaudited cookie surface firing on first
  // paint — the one PECR gap the banner did not yet close.
  //
  // The container id is deliberately env-driven: moving from the inherited
  // Sensolearn container to the Newco one is then a config change, not a code
  // change. If Google Consent Mode is preferred later, this is the single place
  // that changes.
  var containerId = process.env.VUE_APP_GTM_ID || 'GTM-KFCLWBX';
  if (!containerId) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  var script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(containerId);
  document.head.appendChild(script);
}

// On every page load: re-apply whatever the visitor previously chose, so a
// returning visitor who already accepted still gets their tags.
export function applyStoredConsent() {
  if (hasMarketing()) loadMarketing();
  if (hasAnalytics()) loadAnalytics();
}

// After a LIVE consent change (banner Accept/Reject, or footer "Cookie settings"
// withdrawal). setConsent() must be called first. This loads newly granted tags,
// stops withdrawn ones, and broadcasts a "consent-updated" event so consent aware
// components (e.g. the demo video) refresh without needing a manual reload.
export function syncConsent() {
  if (hasMarketing()) {
    loadMarketing();
  } else if (marketingLoaded) {
    // Marketing was running this session and has now been withdrawn. An SPA never
    // reloads on its own, so stop the pixel: revoke it, remove its cookies, and
    // reload. setConsent() has already stored the declined choice, so nothing
    // loads back on after the reload.
    if (window.fbq) { try { window.fbq('consent', 'revoke'); } catch (e) { /* noop */ } }
    deleteCookie('_fbp');
    deleteCookie('_fbc');
    if (typeof window !== 'undefined') {
      window.location.reload();
      return;
    }
  }
  if (hasAnalytics()) loadAnalytics();
  eventBus.$emit('consent-updated');
}
